/** UXP-00013 — Executive Intelligence Platform framework (mirrors pkg/executiveintel) */

export const UXP_EI_ID = 'UXP-00013';

export interface CommandCenterKPI {
  label: string;
  value: string;
  change: string;
  positive: boolean;
}

export interface BriefingRow {
  type: string;
  title: string;
  status: string;
  insights: number;
}

export interface AnalyticsSourceRow {
  source: string;
  health: number;
  records: number;
}

export interface ForecastRow {
  metric: string;
  current: string;
  forecast: string;
  confidence: number;
}

export interface ScenarioRow {
  type: string;
  title: string;
  impact: string;
  confidence: number;
}

export interface DecisionRow {
  title: string;
  priority: number;
  impact: string;
  roi: string;
  confidence: number;
}

export interface InsightRow {
  type: string;
  title: string;
  severity: string;
}

export interface CopilotRec {
  capability: string;
  title: string;
  detail: string;
}

export const COMMAND_CENTER_METRICS = [
  'Business Health', 'Revenue', 'Profit', 'Growth', 'Cash Flow', 'Inventory',
  'Advertising', 'Customer', 'Operations', 'Finance', 'Executive Score', 'Decision Queue',
];

export const BRIEFING_TYPES = [
  'Morning Brief', 'Afternoon Brief', 'Evening Brief', 'Weekly Review', 'Monthly Review',
  'Quarterly Review', 'Annual Review', 'Board Brief', 'Investor Brief',
];

export const ANALYTICS_SOURCES = [
  'Product Intelligence', 'Revenue Intelligence', 'Supply Chain', 'Customer Operations',
  'Financial Intelligence', 'Marketplace Data', 'Advertising', 'AI Insights',
];

export const FORECAST_METRICS = [
  'Revenue', 'Profit', 'Inventory', 'Cash Flow', 'Demand', 'Advertising',
  'Returns', 'Settlement', 'Customer Growth', 'Market Trends',
];

export const SCENARIO_TYPES = [
  'Pricing', 'Advertising', 'Inventory', 'Marketplace Expansion', 'Supplier Delays',
  'Seasonal Events', 'Promotions', 'Tax Changes', 'Operational Decisions',
];

export const DECISION_FEATURES = [
  'Decision Recommendations', 'Priority Scores', 'Business Impact', 'ROI',
  'Risk Analysis', 'Confidence Scores', 'Approval Workflow',
];

export const INSIGHT_TYPES = [
  'Anomalies', 'Patterns', 'Correlations', 'Root Causes', 'Seasonality',
  'Growth Opportunities', 'Operational Risks',
];

export const NL_ANALYTICS_FEATURES = [
  'Conversational Queries', 'Explainable Analytics', 'Business Questions',
  'Trend Analysis', 'Forecast Requests', 'Recommendation Requests',
];

export const REPORT_BUILDER_FEATURES = [
  'Custom Reports', 'Executive Reports', 'Board Reports', 'Investor Reports',
  'Scheduled Reports', 'Export', 'API Access',
];

export const DASHBOARD_BUILDER_FEATURES = [
  'Role-based Dashboards', 'Custom Dashboards', 'Widget Library', 'Saved Views', 'Interactive Drill-down',
];

export const BENCHMARKING_FEATURES = [
  'Historical Performance', 'Marketplace Performance', 'Brand Performance',
  'Category Performance', 'Internal Targets',
];

export const COPILOT_CAPABILITIES = [
  'Strategic Advice', 'Business Explanations', 'Decision Simulations',
  'Risk Assessment', 'Growth Recommendations',
];

export const COLLABORATION_FEATURES = [
  'Executive Notes', 'Assignments', 'Mentions', 'Approvals', 'Decision Logs', 'Meeting Notes',
];

export const ANALYTICS_METRICS = [
  'Forecast Accuracy', 'Decision Accuracy', 'Business Improvements', 'ROI', 'Platform Adoption',
];

export const ACCESSIBILITY_STANDARDS = [
  'WCAG 2.2 AA', 'Keyboard Navigation', 'Screen Reader Support', 'Reduced Motion', 'Responsive Design',
];

export const ARCHITECTURE_STAGES = [
  'Executive Command Center', 'AI Executive Briefings', 'Unified Analytics', 'Forecast Engine',
  'Scenario Simulation', 'Decision Engine', 'AI Insight Engine', 'Natural Language Analytics', 'AI Executive Copilot',
];

export const LIVE_ROUTES: Record<string, string> = {
  executiveHome: '/platform/executive-home',
  analytics: '/analytics',
  command: '/command',
  dailyOps: '/executive/daily-ops',
};

export const DEMO_KPIS: CommandCenterKPI[] = [
  { label: 'Business Health', value: '88/100', change: '+4', positive: true },
  { label: 'Revenue', value: '₹48.2L', change: '+12%', positive: true },
  { label: 'Profit', value: '₹14.6L', change: '+8%', positive: true },
  { label: 'Growth', value: '+14% YoY', change: '+2pp', positive: true },
  { label: 'Cash Flow', value: '₹12.8L', change: '+5%', positive: true },
  { label: 'Inventory', value: '42 days cover', change: 'optimal', positive: true },
  { label: 'Advertising', value: '4.2x ROAS', change: '+0.3x', positive: true },
  { label: 'Customer', value: '4.6 CSAT', change: '+0.2', positive: true },
  { label: 'Operations', value: '96% on-time', change: '+1.1pp', positive: true },
  { label: 'Finance', value: '32% margin', change: '+2pp', positive: true },
  { label: 'Executive Score', value: '91/100', change: '+3', positive: true },
  { label: 'Decision Queue', value: '8 pending', change: '3 urgent', positive: false },
];

export const DEMO_BRIEFINGS: BriefingRow[] = [
  { type: 'Morning Brief', title: 'Morning Brief — Aug 1', status: 'ready', insights: 12 },
  { type: 'Weekly Review', title: 'Weekly Review — W31', status: 'scheduled', insights: 28 },
  { type: 'Board Brief', title: 'Board Brief — Q2', status: 'draft', insights: 42 },
];

export const DEMO_SOURCES: AnalyticsSourceRow[] = [
  { source: 'Revenue Intelligence', health: 98, records: 842000 },
  { source: 'Financial Intelligence', health: 96, records: 124000 },
  { source: 'Supply Chain', health: 94, records: 68000 },
  { source: 'Customer Operations', health: 97, records: 42000 },
];

export const DEMO_FORECASTS: ForecastRow[] = [
  { metric: 'Revenue', current: '₹48.2L', forecast: '₹52.8L', confidence: 87 },
  { metric: 'Profit', current: '₹14.6L', forecast: '₹16.1L', confidence: 82 },
  { metric: 'Demand', current: '842K units', forecast: '920K units', confidence: 85 },
  { metric: 'Cash Flow', current: '₹12.8L', forecast: '₹15.2L', confidence: 79 },
];

export const DEMO_SCENARIOS: ScenarioRow[] = [
  { type: 'Advertising', title: '+10% ad spend on 5.1x ROAS campaigns', impact: '+₹2.4L net profit/mo', confidence: 81 },
  { type: 'Pricing', title: '5% price increase on top 10 SKUs', impact: '+₹1.8L margin · -3% volume', confidence: 76 },
  { type: 'Inventory', title: 'Expedite Hub Gen 3 replenishment', impact: 'Prevent ₹4.2L stockout loss', confidence: 91 },
];

export const DEMO_DECISIONS: DecisionRow[] = [
  { title: 'Approve Prime Day ad budget increase (+15%)', priority: 95, impact: '+₹8.2L revenue', roi: '4.8x', confidence: 88 },
  { title: 'Liquidate 412 aging units before storage fee spike', priority: 92, impact: '+₹1.8L working capital', roi: '2.1x', confidence: 94 },
  { title: 'Launch win-back campaign for 56 at-risk customers', priority: 78, impact: '+₹2.4L LTV protected', roi: '3.2x', confidence: 79 },
];

export const DEMO_INSIGHTS: InsightRow[] = [
  { type: 'Anomalies', title: 'Settlement variance SET-2026-0714 — ₹60K gap detected', severity: 'high' },
  { type: 'Growth Opportunities', title: 'Earbuds Pro category velocity +18% — expand ad coverage', severity: 'opportunity' },
  { type: 'Operational Risks', title: 'Hub Gen 3 stockout risk in 8 days without PO approval', severity: 'critical' },
  { type: 'Patterns', title: 'Tuesday 6 AM IST peak conversion window confirmed', severity: 'info' },
];

export const DEMO_COPILOT: CopilotRec[] = [
  { capability: 'Strategic Advice', title: 'Prioritize Prime Day readiness over new marketplace launch', detail: 'Combined forecast shows 3x ROI on Prime Day prep vs .com expansion in Q3' },
  { capability: 'Risk Assessment', title: 'Cash flow constraint in 21 days if PO-2026-0841 approved today', detail: 'Recommend 5-day delay or partial approval of ₹8L' },
  { capability: 'Growth Recommendations', title: 'Cross-sell Hub Gen 3 to 128 Hub Gen 2 owners', detail: 'Predicted +₹4.1L revenue · 82% confidence · launch before Diwali' },
];

export function buildExecutiveIntelDashboard() {
  return {
    command_center_metrics: COMMAND_CENTER_METRICS.length,
    briefing_types: BRIEFING_TYPES.length,
    analytics_sources: ANALYTICS_SOURCES.length,
    forecast_metrics: FORECAST_METRICS.length,
    scenario_types: SCENARIO_TYPES.length,
  };
}
